/**
 * "Max ctr duration" — the shared cap on how long a counter may run before
 * auto-assign owes the officer a break.
 *
 * Sits in every header whose buttons can trigger an automatic break assignment
 * (Add Officers' shared header, step 4 of the wizard), all showing and setting
 * the one value — so it reads as one setting in several places rather than
 * several settings that happen to agree. Hours and minutes are pickers, not a
 * typed time: the roster only ever works in quarter-hours, and a picker cannot
 * be left half-typed.
 */

import {
  MAX_COUNTER_HOUR_OPTIONS,
  MAX_COUNTER_MINUTE_OPTIONS,
  useMaxCounterDuration,
} from '@/utils/maxCounterDuration';

const selectStyle: React.CSSProperties = {
  padding: '2px 4px',
  border: '1px solid #a5b4fc',
  borderRadius: 5,
  fontSize: 13,
  fontWeight: 700,
  background: 'white',
  color: '#3730a3',
  cursor: 'pointer',
  fontVariantNumeric: 'tabular-nums',
};

export function MaxCounterDurationField({ compact = false }: { compact?: boolean }) {
  const [value, setValue] = useMaxCounterDuration();
  const hours = value.slice(0, 2);
  const minutes = value.slice(2);

  return (
    <label
      title="Longest a counter may run before auto-assign gives the officer a break. An officer whose window is shorter than this works it straight through."
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        // Carries a decision the whole add is planned against, so it is
        // coloured to be found rather than greyed out of the way.
        background: '#eef2ff',
        border: '1px solid #c7d2fe',
        borderRadius: 8,
        padding: '3px 8px',
        fontSize: 12,
        fontWeight: 700,
        color: '#4338ca',
        whiteSpace: 'nowrap',
      }}
    >
      {compact ? 'Max ctr' : 'Max ctr duration'}
      <select
        value={hours}
        onChange={(e) => setValue(`${e.target.value}${minutes}`)}
        aria-label="Max counter duration — hours"
        style={selectStyle}
      >
        {MAX_COUNTER_HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      :
      <select
        value={minutes}
        onChange={(e) => setValue(`${hours}${e.target.value}`)}
        aria-label="Max counter duration — minutes"
        style={selectStyle}
      >
        {MAX_COUNTER_MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
    </label>
  );
}
